import { Link } from 'react-router-dom';
import { useCart } from '@/context/CartContext';
import type { Product } from '@/mocks/products';

interface ProductCardProps {
  product: Product;
}

function StarRating({ rating, count }: { rating: number; count: number }) {
  return (
    <div className="flex items-center gap-1.5">
      <div className="flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map((s) => (
          <i
            key={s}
            className={`text-xs ${s <= Math.floor(rating) ? 'ri-star-fill text-[#C4FF61]' : s - 0.5 <= rating ? 'ri-star-half-fill text-[#C4FF61]' : 'ri-star-line text-gray-300'}`}
          ></i>
        ))}
      </div>
      <span className="text-xs font-inter text-gray-400">({count})</span>
    </div>
  );
}

export default function ProductCard({ product }: ProductCardProps) {
  const { addItem } = useCart();

  return (
    <div className="bg-white rounded-2xl overflow-hidden group transition-all duration-300 hover:-translate-y-1">
      {/* Image */}
      <Link to={`/product/${product.id}`} className="block relative overflow-hidden h-56 cursor-pointer">
        <img
          src={product.images[0]}
          alt={product.name}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
        />
        {product.badge && (
          <span className="absolute top-3 left-3 bg-[#C4FF61] text-[#1A1A1A] font-outfit font-semibold text-xs px-3 py-1 rounded-full">
            {product.badge}
          </span>
        )}
        {!product.inStock && (
          <div className="absolute inset-0 bg-white/60 flex items-center justify-center">
            <span className="font-outfit font-bold text-[#1A1A1A] text-sm bg-white px-4 py-2 rounded-full">Out of Stock</span>
          </div>
        )}
      </Link>

      {/* Info */}
      <div className="p-5">
        <p className="text-xs font-outfit uppercase tracking-widest text-gray-400 mb-1">{product.category}</p>
        <Link to={`/product/${product.id}`} className="cursor-pointer">
          <h3 className="font-outfit font-bold text-base text-[#1A1A1A] leading-snug mb-2 hover:text-[#1A1A1A]/60 transition-colors">
            {product.name}
          </h3>
        </Link>
        <StarRating rating={product.rating} count={product.reviewCount} />

        {/* Specs Tags */}
        <div className="flex flex-wrap gap-1.5 mt-3 mb-4">
          {product.specs.slice(0, 2).map((spec) => (
            <span key={spec} className="text-[10px] font-outfit font-medium text-gray-500 border border-gray-200 px-2 py-0.5 rounded-full whitespace-nowrap">
              {spec}
            </span>
          ))}
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-baseline gap-2">
            <span className="font-outfit font-black text-lg text-[#1A1A1A]">${product.price.toFixed(2)}</span>
            {product.originalPrice && (
              <span className="font-inter text-sm line-through text-gray-300">${product.originalPrice.toFixed(2)}</span>
            )}
          </div>
          <button
            onClick={() => product.inStock && addItem({
              id: product.id,
              name: product.name,
              price: product.price,
              image: product.images[0],
              category: product.category,
            })}
            disabled={!product.inStock}
            className="w-9 h-9 flex items-center justify-center bg-[#C4FF61] text-[#1A1A1A] rounded-full hover:bg-[#a8e84a] transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
            aria-label={`Add ${product.name} to cart`}
          >
            <i className="ri-shopping-cart-line text-sm"></i>
          </button>
        </div>
      </div>
    </div>
  );
}
